import { useEffect, useState } from 'react'

import { api, ErrorApi, type Periodo, type Resumen as DatosResumen, type UtilidadResumen } from '../api'
import { num, pct, usd } from '../formato'
import { GraficoEmbudo } from './GraficoEmbudo'
import { ResumenPendientes } from './ResumenPendientes'

/**
 * La pantalla de inicio. Todo sale de `/api/dashboard` (conteos y sumas de lo guardado).
 * La utilidad agregada la pide aparte y solo si es admin; el servidor la niega (403) a
 * cualquier otro aunque alguien toque este codigo.
 */

interface Props {
  esAdmin: boolean
  alAbrirCliente: (id: string) => void
  alBuscar: (texto: string) => void
}

const PERIODOS: { dias: Periodo; etiqueta: string }[] = [
  { dias: 30, etiqueta: 'Últimos 30 días' },
  { dias: 90, etiqueta: '90 días' },
  { dias: 365, etiqueta: '12 meses' },
]

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`

function Indicador({ titulo, valor, detalle }: { titulo: string; valor: string; detalle?: string }) {
  return (
    <div className="indicador">
      <span className="indicador-titulo">{titulo}</span>
      <span className="indicador-valor">{valor}</span>
      {detalle && <span className="indicador-detalle">{detalle}</span>}
    </div>
  )
}

export function Resumen({ esAdmin, alAbrirCliente, alBuscar }: Props) {
  const [dias, setDias] = useState<Periodo>(30)
  const [datos, setDatos] = useState<DatosResumen | null>(null)
  const [utilidad, setUtilidad] = useState<UtilidadResumen | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setError('')
    api
      .resumen(dias)
      .then(setDatos)
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar el resumen.'))
    if (esAdmin) api.utilidad(dias).then(({ utilidad }) => setUtilidad(utilidad)).catch(() => setUtilidad(null))
  }, [dias, esAdmin])

  const e = datos?.embudo
  const c = datos?.cartera

  return (
    <section>
      <div className="encabezado">
        <div>
          <h1>Resumen</h1>
          {datos && (
            <p className="suave">
              <span className={`insignia ${datos.motor === 'ok' ? 'insignia-cliente' : 'insignia-rechazado'}`}>
                {datos.motor === 'ok' ? 'Motor de cálculo arriba' : 'Motor de cálculo caído: no se puede cotizar'}
              </span>
            </p>
          )}
        </div>
        <div className="filtros">
          {PERIODOS.map((p) => (
            <button key={p.dias} className={p.dias === dias ? 'chip activo' : 'chip'} onClick={() => setDias(p.dias)}>
              {p.etiqueta}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {!datos && !error && <div className="cargando">Cargando el resumen…</div>}

      {e && c && datos && (
        <>
          <div className="indicadores">
            <Indicador titulo="Leads nuevos" valor={num(e.leads_web + e.leads_whatsapp)} detalle={`web ${e.leads_web} · WhatsApp ${e.leads_whatsapp}`} />
            <Indicador titulo="Clientes nuevos" valor={num(e.clientes_nuevos)} detalle={`${plural(e.convertidos, 'vino', 'vinieron')} de un lead`} />
            <Indicador titulo="Clientes cotizados" valor={num(e.clientes_cotizados)} detalle={plural(e.versiones, 'versión guardada', 'versiones guardadas')} />
            <Indicador
              titulo="Tasa de cierre"
              valor={datos.tasa_cierre === null ? '—' : pct(datos.tasa_cierre, 0)}
              detalle={`${plural(e.ganadas, 'ganada', 'ganadas')} · ${plural(e.perdidas, 'perdida', 'perdidas')} (sobre las decididas)`}
            />
            <Indicador titulo="Cartera cotizada" valor={usd(c.total)} detalle={`${c.clientes} clientes · ${num(c.potencia_kwp)} kWp · ${c.pendientes} por decidir`} />
            <Indicador titulo="Total ganado" valor={usd(c.total_ganado)} detalle="con IVA, versiones ganadas en el periodo" />
          </div>

          {esAdmin && utilidad && (
            <div className="tarjeta interno-admin">
              <h2>
                Utilidad SunSmart <span className="insignia insignia-pendiente">solo admin</span>
              </h2>
              <dl className="lista-datos">
                <dt>Cartera cotizada</dt>
                <dd>
                  {usd(utilidad.cartera_utilidad)} de utilidad sobre {usd(utilidad.cartera_costo)} de costo
                </dd>
                <dt>Ganadas</dt>
                <dd>
                  {usd(utilidad.ganadas_utilidad)} de utilidad sobre {usd(utilidad.ganadas_costo)} de costo
                </dd>
              </dl>
            </div>
          )}

          <div className="columnas">
            <div className="tarjeta">
              <h2>Embudo</h2>
              <GraficoEmbudo
                etapas={[
                  { etiqueta: 'Leads', valor: e.leads_web + e.leads_whatsapp, detalle: 'Personas que escribieron por la web o WhatsApp' },
                  { etiqueta: 'Clientes nuevos', valor: e.clientes_nuevos, detalle: `Convertidos de un lead: ${e.convertidos}` },
                  { etiqueta: 'Clientes cotizados', valor: e.clientes_cotizados, detalle: 'Con al menos una versión guardada' },
                  { etiqueta: 'Ganadas', valor: e.ganadas, detalle: 'Versiones marcadas como ganadas' },
                ]}
              />
            </div>
            <div className="tarjeta">
              <h2>Por asesor</h2>
              {datos.asesores.length === 0 ? (
                <p className="suave">Nadie guardó versiones en este periodo.</p>
              ) : (
                <table className="tabla compacta">
                  <thead>
                    <tr>
                      <th>Asesor</th>
                      <th className="num">Versiones</th>
                      <th className="num">Clientes</th>
                      <th className="num">Ganadas</th>
                      <th className="num">Total ganado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.asesores.map((a) => (
                      <tr key={a.usuario_id} className="sin-clic">
                        <td>{a.nombre}</td>
                        <td className="num">{a.versiones}</td>
                        <td className="num">{a.clientes}</td>
                        <td className="num">{a.ganadas}</td>
                        <td className="num">{usd(a.total_ganado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <ResumenPendientes pendientes={datos.pendientes} alAbrirCliente={alAbrirCliente} alBuscar={alBuscar} />
        </>
      )}
    </section>
  )
}
